import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Public privacy policy for theobp.com and the OBP Field mobile app.
 * Draft for operational/store use — have counsel review before wide production release.
 */
const PrivacyPolicyPage: React.FC = () => (
  <div className="container my-5">
    <div className="row justify-content-center">
      <div className="col-lg-9">
        <h1 className="mb-3">Privacy Policy</h1>
        <p className="text-muted">Last updated: October 27, 2026</p>
        <p>
          The Outreach Bible Project (“OBP,” “we,” “us”) operates{' '}
          <a href="https://www.theobp.com">theobp.com</a> and the <strong>OBP Field</strong> mobile
          app (<code>com.theobp.field</code>). This policy describes what information we collect and
          how we use it for ministry event registration, attendee accounts, and field outreach
          operations.
        </p>

        <h2 className="h4 mt-4">Who this covers</h2>
        <ul>
          <li>Visitors and registered users of the website</li>
          <li>Event applicants and attendees</li>
          <li>Users of the OBP Field app (group members, leaders, and admins)</li>
        </ul>

        <h2 className="h4 mt-4">Information we collect</h2>
        <h3 className="h5 mt-3">Account &amp; profile</h3>
        <p>
          Email address, name, and related profile details you provide when creating an account or
          completing an attendee profile (for example phone, emergency contact, church information).
          You may sign in with email/password or a social provider such as Google.
        </p>
        <h3 className="h5 mt-3">Event applications</h3>
        <p>
          Application answers and status associated with outreach events you apply to or attend.
        </p>
        <h3 className="h5 mt-3">OBP Field app — operational data</h3>
        <ul>
          <li>
            <strong>Location (when in use):</strong> optional one-shot location pins you choose to
            share with your group leader (including during SOS). We do not use continuous background
            tracking for this feature.
          </li>
          <li>
            <strong>Push notifications:</strong> a device push token so we can deliver operational
            alerts (for example SOS and related notices).
          </li>
          <li>
            <strong>Group messaging:</strong> messages you send in your event group chat, including
            SOS-related messages.
          </li>
          <li>
            <strong>Itinerary / group assignment:</strong> which field group you are assigned to and
            related schedule stops shown in the app.
          </li>
        </ul>

        <h2 className="h4 mt-4">How we use information</h2>
        <ul>
          <li>Operate event registration, applications, and attendee accounts</li>
          <li>Coordinate field groups, itineraries, messaging, and safety-related SOS alerts</li>
          <li>Authenticate you and keep your session secure</li>
          <li>Communicate about events and ministry operations you are involved in</li>
          <li>Maintain and improve the website and app</li>
        </ul>
        <p>
          We do not sell personal information. We do not use field-app location or chat content for
          advertising.
        </p>

        <h2 className="h4 mt-4">How we share information</h2>
        <p>We share information only as needed to run the ministry and apps, including:</p>
        <ul>
          <li>
            <strong>Service providers</strong> that host or process data on our behalf (for example
            cloud database/auth and push notification delivery)
          </li>
          <li>
            <strong>Event leaders and portal admins</strong> for the events you participate in (group
            roster, itinerary, shared location pins, SOS, and group chat as applicable)
          </li>
          <li>When required by law or to protect safety</li>
        </ul>

        <h2 className="h4 mt-4">Data retention</h2>
        <p>
          We keep account, application, and operational records for as long as needed for ministry
          operations, legal obligations, and safety follow-up. You may request deletion of your
          account data by contacting us (see below), subject to records we must retain.
        </p>

        <h2 className="h4 mt-4">Security</h2>
        <p>
          We use industry-standard safeguards appropriate to our systems (including encrypted
          transport and access controls). No method of transmission or storage is completely secure.
        </p>

        <h2 className="h4 mt-4">Children</h2>
        <p>
          Our services are intended for adults participating in ministry events and related
          volunteers/staff. We do not knowingly collect personal information from children under 13
          for the Field app.
        </p>

        <h2 className="h4 mt-4">Your choices</h2>
        <ul>
          <li>You can decline location permission; sharing a pin is optional</li>
          <li>You can disable notifications in device settings (SOS and other alerts may not arrive)</li>
          <li>You can sign out of the app; we attempt to release that device’s push token on sign-out</li>
        </ul>

        <h2 className="h4 mt-4">Contact</h2>
        <p>
          Privacy questions or deletion requests:
          <br />
          <strong>David Hardin</strong>, Director
          <br />
          <a href="mailto:d_hardin@theobp.com">d_hardin@theobp.com</a>
          <br />
          <a href="tel:770-906-7795">770-906-7795</a>
        </p>
        <p>
          Sending church: Hillcrest Baptist Church, Acworth, Georgia — a ministry supported by
          churches of the American Baptist Association.
        </p>

        <p className="mt-4">
          <Link to="/">← Back to home</Link>
        </p>
      </div>
    </div>
  </div>
);

export default PrivacyPolicyPage;
